/**
 * Validates the `to` target of the staff → portal handoff.
 *
 * The handoff redirect carries a freshly minted staff token in its fragment,
 * so the target must provably be one of our client portals — anything else
 * would hand a usable staff credential to an arbitrary site. Anchored to the
 * portal base domain, whole-label matched (a lookalike suffix like
 * `evilmybcecloud.com` must not pass).
 *
 * @param target - The candidate URL from the `?to=` parameter.
 * @param portalBaseDomain - The tenant wildcard base, e.g. `mybcecloud.com`.
 * @returns True when the target is a portal URL safe to redirect to.
 */
export function isAllowedPortalTarget(target: string, portalBaseDomain: string): boolean {
  const base = portalBaseDomain.trim().toLowerCase();
  if (!base) return false;

  let url: URL;
  try {
    url = new URL(target);
  } catch {
    return false;
  }

  // Embedded credentials are only ever an obfuscation trick in this context.
  if (url.username !== '' || url.password !== '') return false;

  const host = url.hostname.toLowerCase();
  if (host !== base && !host.endsWith(`.${base}`)) return false;

  if (url.protocol === 'https:') return true;
  // Plain http only for the loopback dev bases, never a production portal.
  return url.protocol === 'http:' && (base === 'localtest.me' || base === 'localhost');
}
